/**
 * useSaveLocalEdits — ScheduleDraft 本地编辑批量保存流程。
 *
 * 本 hook 实现 doc §16 §9 定义的保存流程：diff → 批量提交 → 冲突处理 →
 * 状态重置。它不复用 useScheduleDraftEditorMutations 中的单航班 mutation
 * （那些 mutation 含 toast/invalidate/analysis 副作用），而是直接调用 service
 * 层发起 HTTP 请求，完成后统一处理副作用。
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md §9
 * 骨架关联 → hooks/useLocalEditState.js (dirty 数据源),
 *             services/scheduleDraftDataService.js (HTTP 层),
 *             context/ScheduleDraftEditorWorkspaceContext.jsx (保存按钮消费)
 */

import { useCallback, useState } from "react";
import { message } from "antd";
import { scheduleDraftDataService } from "../services/scheduleDraftDataService";
import { buildScheduleDraftFlightPayload } from "../services/scheduleDraftPayloadBuilders";
import { getDraftFlights, getTrackFlights } from "../lib/trackUtils";
import { formatScheduleDraftError } from "../lib/errorUtils";

/**
 * 可保存字段白名单。结构性 change_status 由 CREATE/DELETE 分组处理，
 * 不作为 PATCH payload 字段提交。
 */
const SAVEABLE_FIELDS = [
    "flight_number",
    "aircraft_reg",
    "departure_airport",
    "arrival_airport",
    "std",
    "sta",
    "stc_type",
    "is_fixed",
    "remarks",
    "flight_route_id",
    "block_time_minutes",
    "min_block_time_minutes",
    "display_order",
];
const DELETED_CHANGE_STATUS = "DELETED";

function stripLocalEditMetadata(flight) {
    if (!flight || typeof flight !== "object") {
        return flight;
    }

    const plainFlight = { ...flight };
    Object.keys(plainFlight).forEach((field) => {
        if (field.startsWith("_")) {
            delete plainFlight[field];
        }
    });
    return plainFlight;
}

function findTrackById(draft, trackId) {
    if (!draft?.tracks || trackId == null) {
        return null;
    }
    return draft.tracks.find((track) => String(track?.id) === String(trackId)) || null;
}

function buildWorkingFlights(draft, trackId) {
    const track = findTrackById(draft, trackId);
    return getTrackFlights(track).map(stripLocalEditMetadata);
}

function isEndpointUnavailable(error) {
    const status = error?.response?.status;
    return status === 404 || status === 405 || status === 501;
}

function mergeUpdatedFlightsWithLocalFlights(localFlightsMap, updatedFlights = [], excludedFlightIds = new Set()) {
    const nextFlights = [];
    const seenFlightIds = new Set();
    const updatedFlightsById = new Map(
        updatedFlights
            .filter((flight) => flight?.id != null)
            .map((flight) => [String(flight.id), flight]),
    );

    (localFlightsMap || new Map()).forEach((localFlight, flightId) => {
        const flightIdStr = String(flightId);
        if (excludedFlightIds.has(flightIdStr)) {
            return;
        }
        const updatedFlight = updatedFlightsById.get(flightIdStr);
        seenFlightIds.add(flightIdStr);
        nextFlights.push(stripLocalEditMetadata(updatedFlight ? { ...localFlight, ...updatedFlight } : localFlight));
    });

    updatedFlights.forEach((flight) => {
        if (flight?.id == null || seenFlightIds.has(String(flight.id))) {
            return;
        }
        nextFlights.push(stripLocalEditMetadata(flight));
    });

    return nextFlights;
}

function getFlightChangeStatus(flight) {
    return String(flight?.change_status || "").toUpperCase();
}

function getServerFlight(localEditState, flightId) {
    return localEditState?.serverStateRef?.current?.get?.(String(flightId)) || null;
}

function buildCreateFlightPayload(localFlight) {
    const payload = buildScheduleDraftFlightPayload(localFlight);
    return {
        ...payload,
        id: localFlight.id,
    };
}

/**
 * 从 dirty 航班中提取变更字段，构建精简 payload。
 *
 * @param {Object} localFlight 本地航班（含 _dirty, _dirtyFields）。
 * @returns {Object|null} 仅含 dirty 字段的 payload，无变更返回 null。
 */
function buildDirtyFlightPayload(localFlight) {
    if (!localFlight?._dirty || !localFlight._dirtyFields?.size) {
        return null;
    }

    const payload = {};
    let hasChanges = false;

    localFlight._dirtyFields.forEach((field) => {
        if (SAVEABLE_FIELDS.includes(field)) {
            payload[field] = localFlight[field];
            hasChanges = true;
        }
    });

    // 补充服务端 payload 需要的派生字段（flight_date, day_of_week, change_source）
    if (hasChanges) {
        const fullPayload = buildScheduleDraftFlightPayload({
            ...localFlight,
            ...payload,
        });

        // 只保留 dirty 字段 + 必要的派生字段
        const result = {};
        localFlight._dirtyFields.forEach((field) => {
            if (field in fullPayload) {
                result[field] = fullPayload[field];
            }
        });

        // 始终包含服务端需要的派生字段
        if (fullPayload.flight_date) {
            result.flight_date = fullPayload.flight_date;
        }
        if (fullPayload.day_of_week) {
            result.day_of_week = fullPayload.day_of_week;
        }
        result.change_source = localFlight.change_source === "ENGINE" ? "ENGINE" : "USER";
        result.expected_updated_at = localFlight.updated_at;

        return result;
    }

    return null;
}

/**
 * @typedef {Object} SaveResult
 * @property {"success"|"partial"|"failed"} status 保存结果总状态。
 * @property {Object[]} updatedFlights 成功保存的航班列表。
 * @property {Object[]} conflicts 版本冲突的航班列表。
 * @property {Object[]} errors 保存失败的航班列表。
 */

/**
 * 批量保存本地编辑的 hook。
 *
 * @param {Object} params
 * @param {string|number} params.draftId 草稿 ID。
 * @param {Object} params.localEditState useLocalEditState 返回值。
 * @param {Function} params.refetchDraft detail query refetch。
 * @param {Function} params.refetchAnalysis analysis query refetch。
 * @returns {{
 *   saveAllChanges: () => Promise<SaveResult>,
 *   saving: boolean,
 * }}
 */
export function useSaveLocalEdits({ draftId, draft, localEditState, localCompareMatchState, refetchDraft, refetchAnalysis }) {
    const [saving, setSaving] = useState(false);

    const commitCompareChanges = useCallback(async (sourceDraft = draft) => {
        const dirtyPairs = localCompareMatchState?.getDirtyPairs?.() || [];
        if (!dirtyPairs.length) {
            return { status: "success", committedPairs: [], errors: [] };
        }

        const results = await Promise.allSettled(dirtyPairs.map(async (pair) => {
            const payload = {
                reference_track_id: pair.referenceTrackId,
                working_flights: buildWorkingFlights(sourceDraft, pair.trackId),
                manual_matches: localCompareMatchState.getManualDecisions(pair.trackId, pair.referenceTrackId),
                working_matches: localCompareMatchState.getCommitMatches(pair.trackId, pair.referenceTrackId),
                matches: localCompareMatchState.getCommitMatches(pair.trackId, pair.referenceTrackId),
            };
            const result = await scheduleDraftDataService.commitMatches(draftId, pair.trackId, payload);
            localCompareMatchState.resetPairAfterSave({
                trackId: result?.track_id || pair.trackId,
                referenceTrackId: result?.reference_track_id || pair.referenceTrackId,
                matches: result?.matches || payload.matches,
            });
            return { pair, result };
        }));

        const committedPairs = [];
        const errors = [];
        results.forEach((result) => {
            if (result.status === "fulfilled") {
                committedPairs.push(result.value);
            } else {
                errors.push({ error: result.reason });
            }
        });

        return {
            status: errors.length ? "failed" : "success",
            committedPairs,
            errors,
        };
    }, [draft, draftId, localCompareMatchState]);

    const saveAllChanges = useCallback(async () => {
        const hasFlightDirty = Boolean(localEditState?.dirtyState?.isDirty);
        const hasCompareDirty = Boolean(localCompareMatchState?.dirtyState?.isDirty);

        if (!draftId || (!hasFlightDirty && !hasCompareDirty)) {
            return { status: "success", updatedFlights: [], conflicts: [], errors: [] };
        }

        setSaving(true);

        try {
            const dirtyFlights = hasFlightDirty ? localEditState.getDirtyFlights() : [];
            if (!dirtyFlights.length) {
                const compareResult = await commitCompareChanges(draft);
                if (compareResult.status !== "success") {
                    const firstError = compareResult.errors[0]?.error;
                    const fallback = isEndpointUnavailable(firstError) ? "commit-matches 后端端点尚未可用，Compare 修改未保存" : "保存 Compare 匹配失败";
                    message.error(formatScheduleDraftError(firstError, fallback));
                    return { status: "failed", updatedFlights: [], conflicts: [], errors: compareResult.errors, compareResult };
                }
                if (compareResult.committedPairs.length) {
                    message.success(`已保存 ${compareResult.committedPairs.length} 组 Compare 匹配`);
                }
                await refetchDraft?.().catch(() => null);
                refetchAnalysis?.();
                return { status: "success", updatedFlights: [], conflicts: [], errors: [], compareResult };
            }

            const updatedFlights = [];
            const conflicts = [];
            const errors = [];
            const skippedFlights = [];
            const localOnlyDeletedFlightIds = new Set();

            const createFlights = [];
            const updateFlights = [];
            const deleteFlights = [];

            dirtyFlights.forEach((flight) => {
                const serverFlight = getServerFlight(localEditState, flight.id);
                const isDeleted = getFlightChangeStatus(flight) === DELETED_CHANGE_STATUS;

                if (isDeleted) {
                    if (serverFlight) {
                        deleteFlights.push(flight);
                    } else {
                        skippedFlights.push({ status: "skipped-local-delete", flight });
                        localOnlyDeletedFlightIds.add(String(flight.id));
                    }
                    return;
                }

                if (!serverFlight) {
                    createFlights.push(flight);
                    return;
                }

                updateFlights.push(flight);
            });

            const buildUpdatePromise = async (flight) => {
                const payload = buildDirtyFlightPayload(flight);
                if (!payload) {
                    return { status: "skipped", flight };
                }

                try {
                    const updated = await scheduleDraftDataService.updateFlight(draftId, flight.id, payload);
                    return { status: "updated", flight: updated };
                } catch (error) {
                    const statusCode = error.response?.status;

                    if (statusCode === 409) {
                        return {
                            status: "conflict",
                            flight,
                            error,
                            serverValue: error.response?.data,
                        };
                    }

                    return { status: "error", flight, error };
                }
            };

            const buildCreatePromise = async (flight) => {
                try {
                    const trackId = flight.track_id;
                    if (!trackId) {
                        throw new Error("新增航班缺少编辑轨 ID");
                    }
                    const created = await scheduleDraftDataService.addFlightToTrack(draftId, trackId, buildCreateFlightPayload(flight));
                    return { status: "created", flight: created };
                } catch (error) {
                    return { status: "error", flight, error };
                }
            };

            const buildDeletePromise = async (flight) => {
                try {
                    const deleted = await scheduleDraftDataService.deleteFlight(draftId, flight.id);
                    return { status: "deleted", flight: deleted || flight };
                } catch (error) {
                    return { status: "error", flight, error };
                }
            };

            const collectResults = (results) => {
                results.forEach((result) => {
                    if (result.status === "rejected") {
                        errors.push({ flight: null, error: result.reason });
                        return;
                    }

                    const value = result.value;
                    switch (value.status) {
                        case "created":
                        case "updated":
                        case "deleted":
                            updatedFlights.push(value.flight);
                            break;
                        case "conflict":
                            conflicts.push(value);
                            break;
                        case "error":
                            errors.push(value);
                            break;
                        default:
                            break;
                    }
                });
            };

            // 当前后端无批量端点；按 CREATE -> UPDATE -> DELETE 分组提交，每组内并行。
            collectResults(await Promise.allSettled(createFlights.map(buildCreatePromise)));
            collectResults(await Promise.allSettled(updateFlights.map(buildUpdatePromise)));
            collectResults(await Promise.allSettled(deleteFlights.map(buildDeletePromise)));

            // 根据结果决定状态重置策略
            if (errors.length === 0 && conflicts.length === 0) {
                // 全部成功：refetch 并重置本地状态
                message.success(updatedFlights.length ? `已保存 ${updatedFlights.length} 个航班的修改` : "本地变更已清理");

                let freshDraftData = null;
                try {
                    const freshDraft = await refetchDraft();
                    freshDraftData = freshDraft?.data;
                    const freshFlights = getDraftFlights(freshDraftData);
                    localEditState.resetAfterSave(freshFlights);
                } catch {
                    // refetch 失败时仍视为保存成功，但必须保留未修改航班的工作区快照。
                    localEditState.resetAfterSave(mergeUpdatedFlightsWithLocalFlights(localEditState.localFlightsMap, updatedFlights, localOnlyDeletedFlightIds));
                }

                if (hasCompareDirty) {
                    const compareResult = await commitCompareChanges(freshDraftData || draft);
                    if (compareResult.status !== "success") {
                        const firstError = compareResult.errors[0]?.error;
                        const fallback = isEndpointUnavailable(firstError) ? "commit-matches 后端端点尚未可用，Compare 修改未保存" : "保存 Compare 匹配失败";
                        message.error(formatScheduleDraftError(firstError, fallback));
                        return { status: "failed", updatedFlights, conflicts, errors: compareResult.errors, skippedFlights, compareResult };
                    }
                    if (compareResult.committedPairs.length) {
                        message.success(`已保存 ${compareResult.committedPairs.length} 组 Compare 匹配`);
                    }
                    await refetchDraft?.().catch(() => null);
                }

                refetchAnalysis?.();
                return { status: "success", updatedFlights, conflicts, errors, skippedFlights };
            }

            if (updatedFlights.length > 0) {
                // 部分成功
                message.warning(
                    `${updatedFlights.length} 个航班保存成功，${conflicts.length} 个冲突，${errors.length} 个失败`,
                );
                return { status: "partial", updatedFlights, conflicts, errors, skippedFlights };
            }

            // 全部失败
            const firstError = errors[0]?.error || conflicts[0]?.error;
            message.error(formatScheduleDraftError(firstError, "保存失败"));
            return { status: "failed", updatedFlights, conflicts, errors, skippedFlights };
        } finally {
            setSaving(false);
        }
    }, [commitCompareChanges, draft, draftId, localCompareMatchState?.dirtyState?.isDirty, localEditState, refetchAnalysis, refetchDraft]);

    return {
        saveAllChanges,
        saving,
    };
}

export default useSaveLocalEdits;
